import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api, ApiError, type AgentSendSettings } from '../lib/api'
import { Banner, Button, Card, Chip, SelectField, Switch } from '../ui'

const DAYS = [
  { id: 1, label: 'Mon' },
  { id: 2, label: 'Tue' },
  { id: 3, label: 'Wed' },
  { id: 4, label: 'Thu' },
  { id: 5, label: 'Fri' },
  { id: 6, label: 'Sat' },
  { id: 0, label: 'Sun' },
]

// A time input cannot say 24:00, so "closes at the end of the day" is shown as 23:59 and read back as 1440.
const END_OF_DAY = 24 * 60
const toClock = (minute: number) => {
  const m = Math.min(minute, END_OF_DAY - 1)
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}
const fromClock = (value: string, closing = false) => {
  if (closing && value === '23:59') return END_OF_DAY
  const [h, m] = value.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

/** Every zone the browser knows, with the workspace's own always present even if it is not listed. */
function zoneOptions(current: string): string[] {
  const known = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
  const zones = known.length > 0 ? known : ['Asia/Kolkata', 'UTC', 'Europe/London', 'America/New_York']
  return zones.includes(current) ? zones : [current, ...zones]
}

/**
 * The owner's hand on an agent's throttle: a pause switch that takes effect at once, and the
 * hours and days an agent may start messaging people. These are the workspace's rules, not
 * anything a credential can widen, so they live here rather than on a credential.
 */
export function AgentSendControls() {
  const queryClient = useQueryClient()
  const settings = useQuery({ queryKey: ['agent-send-settings'], queryFn: api.agentSendSettings })
  const [draft, setDraft] = useState<AgentSendSettings | null>(null)

  const loaded = settings.data?.settings
  useEffect(() => {
    if (loaded) setDraft(loaded)
  }, [loaded])

  const save = useMutation({
    mutationFn: (next: AgentSendSettings) => api.saveAgentSendSettings(next),
    onSuccess: async (result) => {
      setDraft(result.settings)
      await queryClient.invalidateQueries({ queryKey: ['agent-send-settings'] })
    },
  })

  if (settings.isError) return null
  if (!settings.data || !draft) return <div className="skeleton" style={{ height: '150px' }} />

  const { open, nextOpensAt, settings: stored } = settings.data
  const dirty =
    draft.startMinute !== stored.startMinute ||
    draft.endMinute !== stored.endMinute ||
    draft.timezone !== stored.timezone ||
    draft.days.join() !== stored.days.join()
  const errorMessage = save.error instanceof ApiError || save.error instanceof Error ? save.error.message : undefined
  const opensText = nextOpensAt
    ? new Date(nextOpensAt).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
    : null

  const toggleDay = (id: number) =>
    setDraft((current) =>
      current
        ? { ...current, days: current.days.includes(id) ? current.days.filter((d) => d !== id) : [...current.days, id].sort() }
        : current,
    )

  return (
    <Card variant="outlined">
      <div className="stack gap-4">
        <div className="row between wrap gap-3">
          <div className="stack gap-1">
            <h2 className="title-large">When agents may send</h2>
            <p className="body-small text-muted" style={{ maxWidth: '60ch' }}>
              These rules apply to any agent that starts a campaign. A send requested outside the hours is
              scheduled for the next opening rather than refused. Replying inside an existing conversation is
              not affected.
            </p>
          </div>
          {stored.paused ? <Chip tone="error">Paused</Chip> : open ? <Chip tone="success">Open now</Chip> : <Chip tone="warn">Closed now</Chip>}
        </div>

        {errorMessage && <Banner tone="error">{errorMessage}</Banner>}

        <div className="row between wrap gap-3">
          <div className="stack gap-1">
            <span className="body-medium" style={{ fontWeight: 560 }}>Pause all agent sending</span>
            <span className="body-small text-muted">
              Nothing new is queued and anything running waits. Turn it back on to resume where it stopped.
            </span>
          </div>
          <Switch
            checked={stored.paused}
            label="Pause all agent sending"
            disabled={save.isPending}
            onChange={(paused) => save.mutate({ ...stored, paused })}
          />
        </div>

        <div className="row gap-3 wrap">
          <label className="field" style={{ minWidth: '120px' }}>
            <span className="field-label">Opens</span>
            <input
              type="time"
              className="field-input"
              value={toClock(draft.startMinute)}
              onChange={(e) => setDraft({ ...draft, startMinute: fromClock(e.target.value) })}
            />
          </label>
          <label className="field" style={{ minWidth: '120px' }}>
            <span className="field-label">Closes</span>
            <input
              type="time"
              className="field-input"
              value={toClock(draft.endMinute)}
              onChange={(e) => setDraft({ ...draft, endMinute: fromClock(e.target.value, true) })}
            />
          </label>
          <SelectField
            label="Timezone"
            value={draft.timezone}
            onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
            style={{ minWidth: '220px' }}
          >
            {zoneOptions(draft.timezone).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </SelectField>
        </div>

        <div className="stack gap-2">
          <span className="label-large">Days</span>
          <div className="row gap-2 wrap">
            {DAYS.map((day) => (
              <Chip key={day.id} selected={draft.days.includes(day.id)} tone={draft.days.includes(day.id) ? 'assist' : undefined} onClick={() => toggleDay(day.id)}>
                {day.label}
              </Chip>
            ))}
          </div>
        </div>

        <div className="row between wrap gap-3">
          <span className="body-small text-muted">
            {stored.paused
              ? 'Paused: no reopening time until you resume.'
              : open
                ? 'Agents may start sends right now.'
                : opensText
                  ? `Next opens ${opensText}.`
                  : ''}
          </span>
          <Button type="button" disabled={!dirty || save.isPending || draft.days.length === 0} onClick={() => save.mutate({ ...draft, paused: stored.paused })}>
            {save.isPending ? 'Saving…' : 'Save hours'}
          </Button>
        </div>
      </div>
    </Card>
  )
}
